import { useQuery } from "@apollo/client";
import {
  AppliedFilters,
  Container,
  Loading,
  Modal,
  Table,
} from "@fluidattacks/design";
import { isNil } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import * as React from "react";
import ReactAnsi from "react-ansi";
import { useTranslation } from "react-i18next";

import { useExploitResultsFilters } from "./filters";
import { columns, getStatus } from "./utils";

import { ExecutionDetails } from "../execution-details";
import { GET_FORCES_EXECUTION } from "../queries";
import type {
  IExecutionModalProps,
  IExploitResult,
  TExecutionTabs,
} from "../types";
import type { ExploitResult, ForcesExecution } from "gql/graphql";
import { useAudit } from "hooks/use-audit";
import { useTable } from "hooks/use-table";

const ExecutionModal = ({
  modalRef,
  execution,
}: IExecutionModalProps): JSX.Element => {
  const { t } = useTranslation();
  const { addAuditEvent } = useAudit();
  const [search, setSearch] = useState("");
  const [filteredDataset, setFilteredDataset] = useState<IExploitResult[]>([]);
  const [currentTab, setCurrentTab] = useState<TExecutionTabs>("summary");
  const padding = 1.5;

  // Query operations
  const { loading, data } = useQuery(GET_FORCES_EXECUTION, {
    onCompleted: (): void => {
      addAuditEvent("ForcesExecution", execution.executionId);
    },
    variables: {
      executionId: execution.executionId,
      groupName: execution.groupName,
    },
  });

  const dataExecution = useMemo((): ForcesExecution | undefined => {
    return data?.forcesExecution as ForcesExecution;
  }, [data?.forcesExecution]);

  const vulns = useMemo((): ExploitResult[] => {
    if (
      isNil(dataExecution?.vulnerabilities) ||
      isNil(dataExecution.vulnerabilities.open) ||
      isNil(dataExecution.vulnerabilities.closed) ||
      isNil(dataExecution.vulnerabilities.accepted)
    ) {
      return [];
    }

    return dataExecution.vulnerabilities.open.concat(
      dataExecution.vulnerabilities.closed.concat(
        dataExecution.vulnerabilities.accepted,
      ),
    );
  }, [dataExecution?.vulnerabilities]);

  const dataset = useMemo(
    (): IExploitResult[] =>
      vulns
        .filter(
          (elem): boolean =>
            elem.where?.toLowerCase().includes(search.toLowerCase()) ??
            elem.who?.toLowerCase().includes(search.toLowerCase()) ??
            elem.kind?.toLowerCase().includes(search.toLowerCase()) ??
            elem.exploitability?.toLowerCase().includes(search.toLowerCase()) ??
            false,
        )
        .map(
          (elem): IExploitResult => ({
            ...elem,
            state: getStatus(elem.state ?? ""),
          }),
        ),
    [search, vulns],
  );

  const { table } = useTable({
    columns,
    data: filteredDataset,
    id: "execution-table",
    options: {
      actionsOptions: { onSearch: setSearch },
      columnOptions: {
        order: ["exploitability", "state", "kind", "who", "where"],
      },
      loading,
      paginationMode: "client-side",
      rowOptions: {
        getId: (item: IExploitResult): string =>
          `${item.where ?? ""}-${item.who ?? ""}-${item.kind ?? ""}-${item.exploitability ?? ""}`,
      },
    },
    total: filteredDataset.length,
  });

  const { Filters, options, removeFilter } = useExploitResultsFilters(
    dataset,
    setFilteredDataset,
  );

  const toTab = useCallback((event: React.MouseEvent): void => {
    event.preventDefault();

    const target = event.target as HTMLButtonElement;

    setCurrentTab(target.id as TExecutionTabs);
  }, []);

  useEffect((): void => {
    setFilteredDataset(dataset);
  }, [dataset]);

  const tabContent: Record<TExecutionTabs, JSX.Element> = {
    log: <ReactAnsi autoScroll={true} log={dataExecution?.log ?? ""} />,
    summary: (
      <Table table={table}>
        <Table.Options>
          <Table.Controls>
            <Filters />
          </Table.Controls>
        </Table.Options>
        <Table.Caption>
          <AppliedFilters onClose={removeFilter} options={options} />
        </Table.Caption>
        <Table.Content />
        <Table.Footer />
      </Table>
    ),
  };

  if (loading) {
    return (
      <Container
        alignItems={"center"}
        display={"flex"}
        height={"100%"}
        justify={"center"}
      >
        <Loading color={"red"} label={"Loading..."} size={"lg"} />
      </Container>
    );
  }

  return (
    <Modal
      modalRef={modalRef}
      size={"md"}
      tabItems={[
        {
          id: "summary",
          isActive: currentTab === "summary",
          label: t("group.forces.tabs.summary.text"),
          link: "/summary",
          onClick: toTab,
          tooltip: t("group.forces.tabs.summary.tooltip"),
        },
        {
          id: "log",
          isActive: currentTab === "log",
          label: t("group.forces.tabs.log.text"),
          link: "/log",
          onClick: toTab,
          tooltip: t("group.forces.tabs.log.tooltip"),
        },
      ]}
      title={t("group.forces.executionDetailsModal.title")}
    >
      {isNil(dataExecution) ? undefined : (
        <Container
          minWidth={"100%"}
          pl={currentTab === "summary" ? padding : 0}
          pr={currentTab === "summary" ? padding : 0}
          scroll={"y"}
        >
          <ExecutionDetails execution={execution} />
          <Container my={2}>{tabContent[currentTab]}</Container>
        </Container>
      )}
    </Modal>
  );
};

export { ExecutionModal };
